import NewPostButton from './NewPostButton'
import styles from './_posts.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import PostItem from './PostItem'
import { useLazyQuery } from '@apollo/client'
import useScrollLoading from '../../../../utils/hooks/useScrollLoading'
import {
  CommunityPost,
  CommunityPostsScopeEnum,
  SortProfileCommunityPostsEnum
} from '../../../../__generated__/graphql'
import { CommunityContext, CommunityPostsScopeMap } from './CommunityLayout'
import CommunitySortDropdown from '../topics/[id]/posts/[post_id]/components/CommunitySortDropdown'
import { COMMUNITY_PROFILE_POSTS_QUERY } from '../../../../gql/queries/community_profile_posts_query'

const postsSortOptions: {
  label: string
  value: SortProfileCommunityPostsEnum
}[] = [
  { label: 'Newest', value: SortProfileCommunityPostsEnum.Newest },
  { label: 'Oldest', value: SortProfileCommunityPostsEnum.Oldest }
]

const PER_PAGE = 16

interface FiltersState {
  page: number
  perPage: number
  orderBy: SortProfileCommunityPostsEnum
  scope: CommunityPostsScopeEnum
}

const ProfilePosts: React.FC = () => {
  const { scope } = useContext(CommunityContext)

  const initialFiltersState: FiltersState = {
    page: 1,
    perPage: PER_PAGE,
    orderBy: SortProfileCommunityPostsEnum.Newest,
    scope
  }

  const [filtersState, setFiltersState] = useState<FiltersState>({
    ...initialFiltersState
  })
  const [searchResult, setSearchResult] = useState<CommunityPost[]>([])

  const [selectedFilter, setSelectedFilter] =
    useState<SortProfileCommunityPostsEnum>(
      SortProfileCommunityPostsEnum.Newest
    )

  const [postsQuery, { data: postsData }] = useLazyQuery(
    COMMUNITY_PROFILE_POSTS_QUERY,
    { fetchPolicy: 'no-cache' }
  )

  const postsListContainerRef = useRef(null)
  const doNewDataRequest = useRef(false)

  const doQuery = (filterState: FiltersState) => {
    postsQuery({ variables: filterState })
  }

  const loadNewDataInBottomPosition = (postsAmount: number) => {
    if (
      !doNewDataRequest.current &&
      postsAmount > 0 &&
      postsAmount % filtersState.perPage == 0
    ) {
      setFiltersState((prevState) => ({
        ...prevState,
        page: prevState.page + 1
      }))
    }
    doNewDataRequest.current = true
  }

  useEffect(() => {
    doQuery(filtersState)
  }, [filtersState])

  useScrollLoading(
    postsListContainerRef,
    () => loadNewDataInBottomPosition(postsData?.communityProfilePosts.length),
    [postsData?.communityProfilePosts]
  )

  useEffect(() => {
    if (postsData) {
      const newPosts = postsData.communityProfilePosts as CommunityPost[]

      setSearchResult((prevPosts) => {
        const allPosts = [...prevPosts, ...newPosts]
        const uniquePosts = Array.from(
          new Map(allPosts.map((post) => [post.id, post])).values()
        )
        return uniquePosts
      })
      doNewDataRequest.current = false
    }
  }, [postsData?.communityProfilePosts])

  useEffect(() => {
    resetSearch()
  }, [selectedFilter])

  const resetSearch = () => {
    setSearchResult([])
    setFiltersState((prevState) => ({
      ...prevState,
      page: 1,
      orderBy: selectedFilter
    }))
  }

  const deleteFromSearchResult = (postId) => {
    setSearchResult((prevSearchResult) =>
      prevSearchResult.filter((post) => post.id !== postId)
    )
  }

  const title = CommunityPostsScopeMap[scope].title
  const description = CommunityPostsScopeMap[scope].description

  return (
    <div className={styles.postsContainer}>
      <div className={styles.header}>
        <div className={styles.topicHeader}>
          <h1>{title}</h1>
          <div className={styles.topicActionsContainer}>
            <div className={styles.topicActions}>
              <CommunitySortDropdown
                activeValue={selectedFilter}
                onSelect={(option: SortProfileCommunityPostsEnum) => {
                  setSelectedFilter(option)
                }}
                options={postsSortOptions}
              />
            </div>
            <NewPostButton />
          </div>
        </div>
        {description && (
          <p className={styles.topicDescription}>{description}</p>
        )}
      </div>

      <div ref={postsListContainerRef}>
        {searchResult?.map((post) => (
          <PostItem
            key={post.id}
            post={post}
            onPostRemove={() => deleteFromSearchResult(post.id)}
          />
        ))}
      </div>
    </div>
  )
}

export default ProfilePosts
